import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

function SOtromov({ usuario }) {
    const [movimientos, setMovimientos] = useState([]);
    const [movimientosFiltrados, setMovimientosFiltrados] = useState([]);
    const [busqueda, setBusqueda] = useState('');

    // Filtros por columna (fila debajo de los encabezados)
    const [filtrosColumna, setFiltrosColumna] = useState({
        noperacion: '',
        fecha: '',
        tipoMov: '',
        total: '',
        operador: '',
        obs: ''
    });

    // Filtro por rango de fechas (barra de acciones)
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [modalAbierto, setModalAbierto] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);

    // Catálogos
    const [tiposMovimiento, setTiposMovimiento] = useState([]);
    const [productos, setProductos] = useState([]);

    // Formulario cabecera
    const [formulario, setFormulario] = useState({
        noperacion: '',
        fecha: '',
        idTipomov: '',
        idDeposito: 1,
        monTotal: 0,
        nasiento: 0,
        idOperador: usuario || '',
        obs: '',
        restado: ' ',
        estado: ' ',
        idProveedor: 0,
        nomProveedor: '',
        ncertificado: 0
    });

    // Detalles
    const [detalles, setDetalles] = useState([]);
    const [detalleTemp, setDetalleTemp] = useState({
        idProducto: '',
        cantidad: 0,
        costoprom: 0,
        monto: 0
    });

    // Estados para selectores tipo cuenta-selector
    const [dropdownTipoMovAbierto, setDropdownTipoMovAbierto] = useState(false);
    const [busquedaTipoMov, setBusquedaTipoMov] = useState('');
    const dropdownTipoMovRef = useRef(null);

    const [dropdownProductoAbierto, setDropdownProductoAbierto] = useState(false);
    const [busquedaProducto, setBusquedaProducto] = useState('');
    const dropdownProductoRef = useRef(null);

    // Estados para paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(10);

    const API_OTROMOV_URL = '/api/otromov';
    const API_TIPOMOV_URL = '/api/tipomov';
    const API_PRODUCTOS_URL = '/api/productos';

    useEffect(() => {
        cargarMovimientos();
        cargarTiposMovimiento();
        cargarProductos();
    }, []);

    useEffect(() => {
        filtrarMovimientos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busqueda, movimientos, filtrosColumna, fechaDesde, fechaHasta]);

    // Cerrar dropdowns al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownTipoMovRef.current && !dropdownTipoMovRef.current.contains(event.target)) {
                setDropdownTipoMovAbierto(false);
            }
            if (dropdownProductoRef.current && !dropdownProductoRef.current.contains(event.target)) {
                setDropdownProductoAbierto(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const cargarMovimientos = async () => {
        try {
            const response = await axios.get(API_OTROMOV_URL);
            setMovimientos(response.data);
            setMovimientosFiltrados(response.data);
        } catch (error) {
            console.error('Error al cargar movimientos:', error);
            alert('Error al cargar los movimientos');
        }
    };

    const cargarTiposMovimiento = async () => {
        try {
            const response = await axios.get(API_TIPOMOV_URL);
            setTiposMovimiento(response.data);
        } catch (error) {
            console.error('Error al cargar tipos de movimiento:', error);
        }
    };

    const cargarProductos = async () => {
        try {
            const response = await axios.get(API_PRODUCTOS_URL);
            setProductos(response.data);
        } catch (error) {
            console.error('Error al cargar productos:', error);
        }
    };

    const filtrarMovimientos = () => {
        const busquedaLower = busqueda ? busqueda.toLowerCase() : '';
        const fc = filtrosColumna;

        // Rango de fechas: comparar en formato ISO YYYY-MM-DD (mov.fecha ya viene asi del backend)
        const desde = fechaDesde || null;
        const hasta = fechaHasta || null;

        const filtrados = movimientos.filter(mov => {
            // Rango de fechas
            if (desde && (!mov.fecha || mov.fecha < desde)) return false;
            if (hasta && (!mov.fecha || mov.fecha > hasta)) return false;

            // Busqueda global (input del header)
            if (busquedaLower) {
                const matchGlobal =
                    mov.noperacion?.toString().includes(busqueda) ||
                    mov.obs?.toLowerCase().includes(busquedaLower) ||
                    obtenerNombreTipoMov(mov.idTipomov)?.toLowerCase().includes(busquedaLower);
                if (!matchGlobal) return false;
            }

            // Filtros por columna (AND entre ellos)
            if (fc.noperacion && !mov.noperacion?.toString().includes(fc.noperacion)) return false;
            if (fc.fecha && !formatearFechaDisplay(mov.fecha).toLowerCase().includes(fc.fecha.toLowerCase())) return false;
            if (fc.tipoMov) {
                const nomTipo = obtenerNombreTipoMov(mov.idTipomov) || '';
                if (!nomTipo.toLowerCase().includes(fc.tipoMov.toLowerCase())) return false;
            }
            if (fc.total && !formatearNumero(mov.monTotal, 2).toLowerCase().includes(fc.total.toLowerCase())) return false;
            if (fc.operador && !(mov.idOperador || '').toLowerCase().includes(fc.operador.toLowerCase())) return false;
            if (fc.obs && !(mov.obs || '').toLowerCase().includes(fc.obs.toLowerCase())) return false;

            return true;
        });

        setMovimientosFiltrados(filtrados);
        setPaginaActual(1); // Resetear a pagina 1 cuando cambia algun filtro
    };

    const limpiarFiltrosColumna = () => {
        setFiltrosColumna({
            noperacion: '', fecha: '', tipoMov: '', total: '', operador: '', obs: ''
        });
    };

    const hayFiltroActivo = Object.values(filtrosColumna).some(v => v && v.length > 0);

    // Funciones de paginación
    const indexUltimoItem = paginaActual * itemsPorPagina;
    const indexPrimerItem = indexUltimoItem - itemsPorPagina;
    const itemsActuales = movimientosFiltrados.slice(indexPrimerItem, indexUltimoItem);
    const totalPaginas = Math.ceil(movimientosFiltrados.length / itemsPorPagina);

    const cambiarPagina = (numeroPagina) => {
        setPaginaActual(numeroPagina);
    };

    const paginaSiguiente = () => {
        if (paginaActual < totalPaginas) {
            setPaginaActual(paginaActual + 1);
        }
    };

    const paginaAnterior = () => {
        if (paginaActual > 1) {
            setPaginaActual(paginaActual - 1);
        }
    };

    const cambiarItemsPorPagina = (e) => {
        setItemsPorPagina(Number(e.target.value));
        setPaginaActual(1);
    };

    const formatearFechaDisplay = (fecha) => {
        if (!fecha) return '';
        const [year, month, day] = fecha.split('-');
        return `${day}/${month}/${year}`;
    };

    const formatearNumero = (valor, decimales = 2) => {
        if (!valor && valor !== 0) return '';
        const numero = parseFloat(valor);
        return numero.toLocaleString(undefined, {
            minimumFractionDigits: decimales,
            maximumFractionDigits: decimales
        });
    };

    const obtenerNombreTipoMov = (idTipomov) => {
        const tipo = tiposMovimiento.find(t => t.idTipomov === idTipomov);
        return tipo ? tipo.nomTipomov : idTipomov;
    };

    const obtenerDescripcionProducto = (idProducto) => {
        const producto = productos.find(p => p.idProducto?.toString() === idProducto?.toString());
        return producto ? producto.descripcion : '';
    };

    // Funciones para selector de tipo de movimiento
    const filtrarTiposMovimiento = (busqueda) => {
        if (!busqueda) return tiposMovimiento;
        const busquedaLower = busqueda.toLowerCase();
        return tiposMovimiento.filter(tipo =>
            tipo.idTipomov?.toLowerCase().includes(busquedaLower) ||
            tipo.nomTipomov?.toLowerCase().includes(busquedaLower)
        );
    };

    const seleccionarTipoMovimiento = (tipo) => {
        setFormulario(prev => ({ ...prev, idTipomov: tipo.idTipomov }));
        setDropdownTipoMovAbierto(false);
        setBusquedaTipoMov('');
    };

    const limpiarTipoMovimiento = () => {
        setFormulario(prev => ({ ...prev, idTipomov: '' }));
        setBusquedaTipoMov('');
    };

    // Funciones para selector de producto en detalle
    const filtrarProductos = (busqueda) => {
        if (!busqueda) return productos.slice(0, 50);
        const busquedaLower = busqueda.toLowerCase();
        return productos.filter(producto =>
            producto.idProducto?.toString().includes(busquedaLower) ||
            producto.descripcion?.toLowerCase().includes(busquedaLower)
        ).slice(0, 50);
    };

    const seleccionarProducto = (producto) => {
        setDetalleTemp(prev => ({
            ...prev,
            idProducto: producto.idProducto.toString(),
            costoprom: producto.costoprom || 0
        }));
        setDropdownProductoAbierto(false);
        setBusquedaProducto('');
    };

    const limpiarProducto = () => {
        setDetalleTemp(prev => ({ ...prev, idProducto: '', costoprom: 0 }));
        setBusquedaProducto('');
    };

    const handleChangeFormulario = (e) => {
        const { name, value } = e.target;
        setFormulario(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeDetalleTemp = (e) => {
        const { name, value } = e.target;
        let newDetalleTemp = { ...detalleTemp, [name]: value };

        // Calcular monto automáticamente
        if (name === 'cantidad' || name === 'costoprom') {
            const cantidad = name === 'cantidad' ? parseFloat(value) || 0 : parseFloat(detalleTemp.cantidad) || 0;
            const costoprom = name === 'costoprom' ? parseFloat(value) || 0 : parseFloat(detalleTemp.costoprom) || 0;
            newDetalleTemp.monto = (cantidad * costoprom);
        }

        setDetalleTemp(newDetalleTemp);
    };

    const agregarDetalle = () => {
        if (!detalleTemp.idProducto) {
            alert('Debe seleccionar un producto');
            return;
        }

        if (parseFloat(detalleTemp.cantidad) <= 0) {
            alert('La cantidad debe ser mayor a 0');
            return;
        }

        // Verificar si el producto ya existe en detalles
        const existe = detalles.find(d => d.idProducto === detalleTemp.idProducto);
        if (existe) {
            alert('Este producto ya está en la lista');
            return;
        }

        setDetalles(prev => [...prev, { ...detalleTemp }]);
        setDetalleTemp({
            idProducto: '',
            cantidad: 0,
            costoprom: 0,
            monto: 0
        });
        setBusquedaProducto('');
    };

    const eliminarDetalle = (idProducto) => {
        setDetalles(prev => prev.filter(d => d.idProducto !== idProducto));
    };

    const calcularTotal = () => {
        return detalles.reduce((sum, detalle) => sum + parseFloat(detalle.monto || 0), 0);
    };

    const calcularTotalFormateado = () => {
        return formatearNumero(calcularTotal(), 2);
    };

    const abrirModalNuevo = () => {
        const ahora = new Date();
        const hoy = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
        setFormulario({
            noperacion: '',
            fecha: hoy,
            idTipomov: '',
            idDeposito: 1,
            monTotal: 0,
            nasiento: 0,
            idOperador: usuario || '',
            obs: '',
            restado: ' ',
            estado: ' ',
            idProveedor: 0,
            nomProveedor: '',
            ncertificado: 0
        });
        setDetalles([]);
        setDetalleTemp({
            idProducto: '',
            cantidad: 0,
            costoprom: 0,
            monto: 0
        });
        setModoEdicion(false);
        setModalAbierto(true);
    };

    const abrirModalEditar = (movimiento) => {
        setFormulario({
            noperacion: movimiento.noperacion,
            fecha: movimiento.fecha,
            idTipomov: movimiento.idTipomov,
            idDeposito: movimiento.idDeposito || 1,
            monTotal: movimiento.monTotal || 0,
            nasiento: movimiento.nasiento || 0,
            idOperador: movimiento.idOperador || usuario,
            obs: movimiento.obs || '',
            restado: movimiento.restado || ' ',
            estado: movimiento.estado || ' ',
            idProveedor: movimiento.idProveedor || 0,
            nomProveedor: movimiento.nomProveedor || '',
            ncertificado: movimiento.ncertificado || 0
        });
        setDetalles(movimiento.detalles || []);
        setModoEdicion(true);
        setModalAbierto(true);
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        setFormulario({
            noperacion: '',
            fecha: '',
            idTipomov: '',
            idDeposito: 1,
            monTotal: 0,
            nasiento: 0,
            idOperador: usuario || '',
            obs: '',
            restado: ' ',
            estado: ' ',
            idProveedor: 0,
            nomProveedor: '',
            ncertificado: 0
        });
        setDetalles([]);
        setDetalleTemp({
            idProducto: '',
            cantidad: 0,
            costoprom: 0,
            monto: 0
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formulario.fecha) {
            alert('La fecha es requerida');
            return;
        }

        if (!formulario.idTipomov) {
            alert('Debe seleccionar un tipo de movimiento');
            return;
        }

        if (detalles.length === 0) {
            alert('Debe agregar al menos un detalle');
            return;
        }

        // Preparar datos para enviar
        const datosEnviar = {
            fecha: formulario.fecha,
            idTipomov: formulario.idTipomov,
            idDeposito: parseInt(formulario.idDeposito) || null,
            monTotal: parseFloat(calcularTotal()),
            nasiento: parseInt(formulario.nasiento) || null,
            idOperador: formulario.idOperador,
            obs: formulario.obs || '',
            restado: formulario.restado || ' ',
            estado: formulario.estado || ' ',
            idProveedor: parseInt(formulario.idProveedor) || 0,
            nomProveedor: formulario.nomProveedor || '',
            ncertificado: parseInt(formulario.ncertificado) || 0,
            detalles: detalles.map(d => ({
                idProducto: parseInt(d.idProducto),
                cantidad: parseFloat(d.cantidad),
                costoprom: parseFloat(d.costoprom),
                monto: parseFloat(d.monto)
            }))
        };

        // En modo edición, agregar noperacion
        if (modoEdicion) {
            datosEnviar.noperacion = formulario.noperacion;
        }

        try {
            if (modoEdicion) {
                await axios.put(`${API_OTROMOV_URL}/${formulario.noperacion}`, datosEnviar);
                alert('Movimiento actualizado exitosamente');
            } else {
                await axios.post(API_OTROMOV_URL, datosEnviar);
                alert('Movimiento creado exitosamente');
            }
            cargarMovimientos();
            cerrarModal();
        } catch (error) {
            console.error('Error completo:', error);
            console.error('Error response:', error.response?.data);

            let mensajeError = 'Error al guardar el movimiento';
            if (error.response?.data?.message) {
                mensajeError += ': ' + error.response.data.message;
            } else if (error.response?.data) {
                mensajeError += ': ' + JSON.stringify(error.response.data);
            } else if (error.message) {
                mensajeError += ': ' + error.message;
            }

            alert(mensajeError);
        }
    };

    const eliminar = async (noperacion) => {
        if (window.confirm('¿Está seguro de eliminar este movimiento?')) {
            try {
                await axios.delete(`${API_OTROMOV_URL}/${noperacion}`);
                alert('Movimiento eliminado exitosamente');
                cargarMovimientos();
            } catch (error) {
                console.error('Error al eliminar:', error);
                alert('Error al eliminar el movimiento');
            }
        }
    };

    const exportarExcel = () => {
        const datosExportar = movimientosFiltrados.map(mov => ({
            'N° Operación': mov.noperacion,
            'Fecha': formatearFechaDisplay(mov.fecha),
            'Tipo Movimiento': obtenerNombreTipoMov(mov.idTipomov),
            'Total': parseFloat(mov.monTotal || 0),
            'Operador': mov.idOperador,
            'Observaciones': mov.obs
        }));

        const ws = XLSX.utils.json_to_sheet(datosExportar);

        // Aplicar formato de número a la columna Total (columna D, índice 3)
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let row = range.s.r + 1; row <= range.e.r; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 3 }); // Columna D (Total)
            if (ws[cellAddress]) {
                ws[cellAddress].z = '#,##0.00'; // Formato numérico con 2 decimales
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');
        XLSX.writeFile(wb, 'otros_movimientos.xlsx');
    };

    return (
        <div className="gestion-producto-container">
            <div className="header-seccion">
                <h2>Otros Movimientos</h2>
                <div className="acciones-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Rango de fechas */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #ddd', borderRadius: '5px', padding: '4px 8px', backgroundColor: '#fff' }}>
                        <label style={{ fontSize: '12px', color: '#555', whiteSpace: 'nowrap' }}>📅 Desde</label>
                        <input
                            type="date"
                            value={fechaDesde}
                            onChange={(e) => setFechaDesde(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
                        />
                        <label style={{ fontSize: '12px', color: '#555', whiteSpace: 'nowrap' }}>Hasta</label>
                        <input
                            type="date"
                            value={fechaHasta}
                            onChange={(e) => setFechaHasta(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
                        />
                        {(fechaDesde || fechaHasta) && (
                            <button
                                type="button"
                                onClick={() => { setFechaDesde(''); setFechaHasta(''); }}
                                title="Limpiar rango de fechas"
                                style={{ padding: '2px 6px', border: 'none', backgroundColor: 'transparent', color: '#d97706', cursor: 'pointer', fontSize: '14px' }}
                            >✕</button>
                        )}
                    </div>
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="input-busqueda"
                    />
                    <button onClick={abrirModalNuevo} className="btn btn-nuevo">
                        + Nuevo Movimiento
                    </button>
                    <button onClick={exportarExcel} className="btn btn-exportar">
                        📊 Exportar Excel
                    </button>
                </div>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>N° Operación</th>
                            <th>Fecha</th>
                            <th>Tipo Movimiento</th>
                            <th>Total</th>
                            <th>Operador</th>
                            <th>Observaciones</th>
                            <th>Acciones</th>
                        </tr>
                        {/* Fila de filtros por columna */}
                        <tr style={{ backgroundColor: '#F5F7FB' }}>
                            <th style={{ padding: '4px' }}>
                                <input type="text" value={filtrosColumna.noperacion}
                                    onChange={(e) => setFiltrosColumna({ ...filtrosColumna, noperacion: e.target.value })}
                                    placeholder="🔍"
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', fontWeight: 'normal' }} />
                            </th>
                            <th style={{ padding: '4px' }}>
                                <input type="text" value={filtrosColumna.fecha}
                                    onChange={(e) => setFiltrosColumna({ ...filtrosColumna, fecha: e.target.value })}
                                    placeholder="🔍 dd/mm/aaaa"
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', fontWeight: 'normal' }} />
                            </th>
                            <th style={{ padding: '4px' }}>
                                <input type="text" value={filtrosColumna.tipoMov}
                                    onChange={(e) => setFiltrosColumna({ ...filtrosColumna, tipoMov: e.target.value })}
                                    placeholder="🔍 tipo mov"
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', fontWeight: 'normal' }} />
                            </th>
                            <th style={{ padding: '4px' }}>
                                <input type="text" value={filtrosColumna.total}
                                    onChange={(e) => setFiltrosColumna({ ...filtrosColumna, total: e.target.value })}
                                    placeholder="🔍"
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', textAlign: 'right', fontWeight: 'normal' }} />
                            </th>
                            <th style={{ padding: '4px' }}>
                                <input type="text" value={filtrosColumna.operador}
                                    onChange={(e) => setFiltrosColumna({ ...filtrosColumna, operador: e.target.value })}
                                    placeholder="🔍"
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', fontWeight: 'normal' }} />
                            </th>
                            <th style={{ padding: '4px' }}>
                                <input type="text" value={filtrosColumna.obs}
                                    onChange={(e) => setFiltrosColumna({ ...filtrosColumna, obs: e.target.value })}
                                    placeholder="🔍"
                                    style={{ width: '100%', padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', fontWeight: 'normal' }} />
                            </th>
                            <th style={{ padding: '4px', textAlign: 'center' }}>
                                {hayFiltroActivo && (
                                    <button type="button" onClick={limpiarFiltrosColumna}
                                        title="Limpiar filtros de columna"
                                        style={{
                                            padding: '4px 8px', border: '1px solid #d97706',
                                            backgroundColor: '#fff7ed', color: '#d97706',
                                            borderRadius: '4px', cursor: 'pointer',
                                            fontSize: '12px', fontWeight: 'normal'
                                        }}>
                                        ✕ Limpiar
                                    </button>
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {itemsActuales.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="sin-datos">No hay movimientos para mostrar</td>
                            </tr>
                        ) : (
                            itemsActuales.map(mov => (
                                <tr key={mov.noperacion}>
                                    <td>{mov.noperacion}</td>
                                    <td>{formatearFechaDisplay(mov.fecha)}</td>
                                    <td>{obtenerNombreTipoMov(mov.idTipomov)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatearNumero(mov.monTotal, 2)}</td>
                                    <td>{mov.idOperador}</td>
                                    <td>{mov.obs}</td>
                                    <td>
                                        <button
                                            onClick={() => abrirModalEditar(mov)}
                                            className="btn-accion"
                                            title="Editar"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => eliminar(mov.noperacion)}
                                            className="btn-accion"
                                            title="Eliminar"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Controles de paginación */}
            <div className="paginacion-container" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 20px',
                borderTop: '1px solid #ddd'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label>Mostrar:</label>
                    <select value={itemsPorPagina} onChange={cambiarItemsPorPagina} className="select-items">
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <span>por página</span>
                </div>

                <div className="paginacion-info">
                    Mostrando {indexPrimerItem + 1} - {Math.min(indexUltimoItem, movimientosFiltrados.length)} de {movimientosFiltrados.length} movimientos
                </div>

                <div className="paginacion-controles" style={{ display: 'flex', gap: '5px' }}>
                    <button
                        onClick={paginaAnterior}
                        disabled={paginaActual === 1}
                        className="btn-paginacion"
                    >
                        ‹ Anterior
                    </button>

                    {[...Array(totalPaginas)].map((_, index) => {
                        const numeroPagina = index + 1;
                        // Mostrar solo algunas páginas alrededor de la actual
                        if (
                            numeroPagina === 1 ||
                            numeroPagina === totalPaginas ||
                            (numeroPagina >= paginaActual - 2 && numeroPagina <= paginaActual + 2)
                        ) {
                            return (
                                <button
                                    key={numeroPagina}
                                    onClick={() => cambiarPagina(numeroPagina)}
                                    className={`btn-numero-pagina ${paginaActual === numeroPagina ? 'activo' : ''}`}
                                >
                                    {numeroPagina}
                                </button>
                            );
                        } else if (
                            numeroPagina === paginaActual - 3 ||
                            numeroPagina === paginaActual + 3
                        ) {
                            return <span key={numeroPagina}>...</span>;
                        }
                        return null;
                    })}

                    <button
                        onClick={paginaSiguiente}
                        disabled={paginaActual === totalPaginas}
                        className="btn-paginacion"
                    >
                        Siguiente ›
                    </button>
                </div>
            </div>

            {/* Modal */}
            {modalAbierto && (
                <div className="modal-overlay">
                    <div className="modal-contenido-grande" style={{ maxWidth: '1000px' }}>
                        <div className="modal-header">
                            <h3>{modoEdicion ? 'Editar Movimiento' : 'Nuevo Movimiento'}</h3>
                            <button onClick={cerrarModal} className="btn-cerrar">×</button>
                        </div>

                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                {/* Cabecera */}
                                <div className="form-section">
                                    <h3>Datos de Cabecera</h3>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                                        {modoEdicion && (
                                            <div className="form-group">
                                                <label>N° Operación</label>
                                                <input
                                                    type="text"
                                                    value={formulario.noperacion}
                                                    className="form-input"
                                                    disabled
                                                />
                                            </div>
                                        )}

                                        <div className="form-group">
                                            <label>Fecha *</label>
                                            <input
                                                type="date"
                                                name="fecha"
                                                value={formulario.fecha}
                                                onChange={handleChangeFormulario}
                                                className="form-input"
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>Tipo Movimiento *</label>
                                            <div className="cuenta-selector" ref={dropdownTipoMovRef}>
                                                <input
                                                    type="text"
                                                    value={formulario.idTipomov ? '' : busquedaTipoMov}
                                                    onChange={(e) => {
                                                        setBusquedaTipoMov(e.target.value);
                                                        setDropdownTipoMovAbierto(true);
                                                    }}
                                                    onFocus={() => setDropdownTipoMovAbierto(true)}
                                                    placeholder="Buscar tipo de movimiento..."
                                                    className="form-input"
                                                />
                                                {formulario.idTipomov && (
                                                    <div className="cuenta-seleccionada">
                                                        <span className="cuenta-numero">{formulario.idTipomov}</span>
                                                        <span className="cuenta-descripcion">({obtenerNombreTipoMov(formulario.idTipomov)})</span>
                                                        <button
                                                            type="button"
                                                            onClick={limpiarTipoMovimiento}
                                                            className="btn-clear-cuenta"
                                                        >
                                                            ✖
                                                        </button>
                                                    </div>
                                                )}
                                                {dropdownTipoMovAbierto && (
                                                    <div className="cuenta-dropdown">
                                                        {filtrarTiposMovimiento(busquedaTipoMov).length === 0 ? (
                                                            <div className="cuenta-item-vacio">No se encontraron tipos de movimiento</div>
                                                        ) : (
                                                            filtrarTiposMovimiento(busquedaTipoMov).map(tipo => (
                                                                <div
                                                                    key={tipo.idTipomov}
                                                                    onClick={() => seleccionarTipoMovimiento(tipo)}
                                                                    className="cuenta-item"
                                                                >
                                                                    <span className="cuenta-descripcion">{tipo.nomTipomov}</span>
                                                                    <span className="cuenta-numero">({tipo.idTipomov})</span>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>Operador</label>
                                            <input
                                                type="text"
                                                name="idOperador"
                                                value={formulario.idOperador}
                                                className="form-input"
                                                disabled
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Observaciones</label>
                                        <textarea
                                            name="obs"
                                            value={formulario.obs}
                                            onChange={handleChangeFormulario}
                                            className="form-input"
                                            rows="3"
                                            maxLength="2500"
                                        />
                                    </div>
                                </div>

                                {/* Detalles */}
                                <div className="form-section" style={{ marginTop: '20px' }}>
                                    <h3>Detalles del Movimiento</h3>

                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                                        <div className="form-group">
                                            <label>Producto *</label>
                                            <div className="cuenta-selector" ref={dropdownProductoRef}>
                                                <input
                                                    type="text"
                                                    value={detalleTemp.idProducto ? '' : busquedaProducto}
                                                    onChange={(e) => {
                                                        setBusquedaProducto(e.target.value);
                                                        setDropdownProductoAbierto(true);
                                                    }}
                                                    onFocus={() => setDropdownProductoAbierto(true)}
                                                    placeholder="Buscar producto..."
                                                    className="form-input"
                                                />
                                                {detalleTemp.idProducto && (
                                                    <div className="cuenta-seleccionada">
                                                        <span className="cuenta-numero">{detalleTemp.idProducto}</span>
                                                        <span className="cuenta-descripcion">({obtenerDescripcionProducto(detalleTemp.idProducto)})</span>
                                                        <button
                                                            type="button"
                                                            onClick={limpiarProducto}
                                                            className="btn-clear-cuenta"
                                                        >
                                                            ✖
                                                        </button>
                                                    </div>
                                                )}
                                                {dropdownProductoAbierto && (
                                                    <div className="cuenta-dropdown">
                                                        {filtrarProductos(busquedaProducto).length === 0 ? (
                                                            <div className="cuenta-item-vacio">No se encontraron productos</div>
                                                        ) : (
                                                            filtrarProductos(busquedaProducto).map(producto => (
                                                                <div
                                                                    key={producto.idProducto}
                                                                    onClick={() => seleccionarProducto(producto)}
                                                                    className="cuenta-item"
                                                                >
                                                                    <span className="cuenta-descripcion">{producto.descripcion}</span>
                                                                    <span className="cuenta-numero">({producto.idProducto})</span>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label>Cantidad</label>
                                            <input
                                                type="number"
                                                name="cantidad"
                                                value={detalleTemp.cantidad}
                                                onChange={handleChangeDetalleTemp}
                                                step="0.001"
                                                className="form-input"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>Costo</label>
                                            <input
                                                type="number"
                                                name="costoprom"
                                                value={detalleTemp.costoprom}
                                                onChange={handleChangeDetalleTemp}
                                                step="0.01"
                                                className="form-input"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>Monto</label>
                                            <input
                                                type="text"
                                                value={formatearNumero(detalleTemp.monto, 2)}
                                                className="form-input"
                                                disabled
                                                style={{ textAlign: 'right' }}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <button
                                                type="button"
                                                onClick={agregarDetalle}
                                                className="btn btn-nuevo"
                                                style={{ marginTop: '5px' }}
                                            >
                                                + Agregar
                                            </button>
                                        </div>
                                    </div>

                                    {/* Tabla de detalles */}
                                    <div style={{ marginTop: '15px' }}>
                                        <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                            <thead>
                                                <tr>
                                                    <th>ID Producto</th>
                                                    <th>Descripción</th>
                                                    <th>Cantidad</th>
                                                    <th>Costo</th>
                                                    <th>Monto</th>
                                                    <th>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detalles.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="sin-datos">No hay detalles agregados</td>
                                                    </tr>
                                                ) : (
                                                    detalles.map((detalle, index) => (
                                                        <tr key={index}>
                                                            <td>{detalle.idProducto}</td>
                                                            <td>{obtenerDescripcionProducto(detalle.idProducto)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatearNumero(detalle.cantidad, 3)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatearNumero(detalle.costoprom, 2)}</td>
                                                            <td style={{ textAlign: 'right' }}>{formatearNumero(detalle.monto, 2)}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => eliminarDetalle(detalle.idProducto)}
                                                                    className="btn-accion"
                                                                    title="Eliminar"
                                                                >
                                                                    🗑️
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                    <td colSpan="4" style={{ textAlign: 'right' }}>TOTAL:</td>
                                                    <td style={{ textAlign: 'right' }}>{calcularTotalFormateado()}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button type="button" onClick={cerrarModal} className="btn-secondary">
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        {modoEdicion ? 'Actualizar' : 'Guardar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SOtromov;
